import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useTransition
} from 'react';
import {
  DialogSecondaryAction,
  useCurrentUser,
  useDialog,
  useSnackbar
} from '@ianesthesie-kit/redux';
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack
} from '@mui/material';
import {
  TranslatedText,
  useCountdown,
  useTranslator
} from '@ianesthesie-kit/core';
import { AnimatePresence, motion } from 'framer-motion';
import { Loader } from '@/components/loader';
import { AuthService } from '@ianesthesie-kit/services';
import { SentryService } from '@ianesthesie-kit/sentry';
import { AppConfigService } from '@/config';
import { matchIsValidTel } from '@ianesthesie-kit/tel-input';
import { TelInput } from '@/components/telInput';
import Clear from '@mui/icons-material/Clear';
import { StandardDialogContainer } from './common';

// TODO replace this with OtpInputWithControls component
import { LegacyOtpInput } from '@/components/otp';

type OtpStep =
  | 'initial'
  | 'updatePhoneNumber'
  | 'requestingOtp'
  | 'otpSent'
  | 'failure'
  | 'decline';

const CLOSE_TIMEOUT = 3000;
export const PhoneVerificationDialog = ({
  initialPrompt,
  updatePhone
}: {
  initialPrompt?: boolean;
  updatePhone?: boolean;
}) => {
  const user = useCurrentUser();
  const { closeDialog } = useDialog();
  const { showSnackbar } = useSnackbar();
  const translate = useTranslator();
  const [newNumber, setNewNumber] = useState<string>();
  const {
    countdown,
    isStarted,
    isRunning,
    start: startCountdown,
    cancel
  } = useCountdown(CLOSE_TIMEOUT, {
    intervalMs: 200,
    autoStart: false
  });
  const reqMade = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const [, startTransition] = useTransition();
  const [step, setStep] = useState<OtpStep>(
    initialPrompt
      ? 'initial'
      : updatePhone
      ? 'updatePhoneNumber'
      : 'requestingOtp'
  );

  const close = useCallback(() => {
    cancel();
    closeDialog();
  }, [cancel, closeDialog]);

  const requestOtp = useCallback(
    (newPhone?: string) => {
      if (reqMade.current) {
        return Promise.resolve();
      }
      const phone = newPhone ?? user?.phoneNumber;
      if (!phone) {
        return Promise.reject(new Error('No phone number present'));
      }
      reqMade.current = true;
      setStep('requestingOtp');
      return AuthService.getInstance()
        .requestPhoneVerification({
          phone
        })
        .then(() => {
          setStep('otpSent');
        })
        .catch((err) => {
          setStep('failure');
          SentryService.getInstance().captureException(err);
        })
        .finally(() => {
          reqMade.current = false;
        });
    },
    [user?.phoneNumber]
  );

  useEffect(() => {
    if (step === 'decline') {
      if (!isStarted) {
        startCountdown();
        startTransition(() => {
          AppConfigService.getInstance().phoneVerificationPromptDeclined();
        });
      } else if (!isRunning) {
        closeDialog();
      }
    }
  }, [step, startCountdown, isStarted, isRunning, closeDialog]);

  useEffect(() => {
    if (!initialPrompt && !updatePhone) {
      requestOtp();
    }
  }, [initialPrompt, updatePhone, requestOtp]);

  const submitOtp = (otp: string) => {
    setSubmitting(true);

    return AuthService.getInstance()
      .verifyPhone({ code: otp })
      .then(() => {
        closeDialog();
        showSnackbar({
          message: translate('patient-app.verifyPhoneNumberSuccessSnackbar'),
          severity: 'success',
          position: {
            vertical: 'top',
            horizontal: 'center'
          }
        });
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  if (user?.phoneNumberVerified || (!user?.phoneNumber && !updatePhone)) {
    closeDialog();
    // TODO sentry report?
    return null;
  }

  const stepFlag = step === 'decline' || step === 'updatePhoneNumber';

  return (
    <StandardDialogContainer>
      <DialogTitle>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.div
              key={stepFlag ? step : 'initial'}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            >
              <TranslatedText
                id={
                  step === 'updatePhoneNumber'
                    ? 'patient-app.profile.updatePhoneModal.title'
                    : `patient-app.auth.phoneVerificationDialog${
                        !stepFlag ? '' : `.${step}`
                      }.title`
                }
              />
            </motion.div>
          </AnimatePresence>
          <Box position="relative" bottom={0} left={8}>
            <IconButton onClick={closeDialog} size="small">
              <Clear />
            </IconButton>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ position: 'relative', minHeight: 80 }}>
        {submitting && <Loader />}
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.div
            key={step}
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -10, opacity: 0 }}
          >
            {step === 'otpSent' && (
              <LegacyOtpInput
                onComplete={submitOtp}
                onResend={() => requestOtp()}
              />
            )}
            {step === 'requestingOtp' && <Loader />}
            {step !== 'otpSent' && step !== 'requestingOtp' && (
              <DialogContentText>
                <TranslatedText
                  id={
                    step === 'updatePhoneNumber'
                      ? 'patient-app.profile.updatePhoneModal.body'
                      : `patient-app.auth.phoneVerificationDialog.${step}`
                  }
                />
                {step === 'initial' && (
                  <span style={{ display: 'block' }}>
                    <TranslatedText id="patient-app.auth.phoneVerificationDialog.initial2" />
                  </span>
                )}
                {step === 'updatePhoneNumber' && (
                  <span style={{ display: 'block' }}>
                    <TelInput
                      fullWidth
                      error={!!newNumber && !matchIsValidTel(newNumber)}
                      label=""
                      value={newNumber ?? ''}
                      onChange={(v, info) => {
                        if (
                          info.numberValue &&
                          info.countryCallingCode &&
                          info.countryCallingCode !== info.numberValue
                        ) {
                          setNewNumber(info.numberValue);
                        } else {
                          setNewNumber(undefined);
                        }
                      }}
                    />
                  </span>
                )}
              </DialogContentText>
            )}
          </motion.div>
        </AnimatePresence>
      </DialogContent>
      <AnimatePresence initial={false}>
        {step !== 'otpSent' && step !== 'requestingOtp' && (
          <motion.div layout="size" exit={{ maxHeight: 0 }}>
            <DialogActions>
              <Stack direction="row" gap={1}>
                {step === 'initial' && (
                  <>
                    <DialogSecondaryAction onClick={() => setStep('decline')}>
                      <TranslatedText id="patient-app.auth.phoneVerificationDialog.cancel" />
                    </DialogSecondaryAction>
                    <Button onClick={() => requestOtp()}>
                      <TranslatedText id="patient-app.auth.phoneVerificationDialog.accept" />
                    </Button>
                  </>
                )}
                {step === 'updatePhoneNumber' && (
                  <>
                    <DialogSecondaryAction onClick={closeDialog}>
                      <TranslatedText id="app.cancel" />
                    </DialogSecondaryAction>
                    <Button
                      disabled={!newNumber || !matchIsValidTel(newNumber)}
                      onClick={() => requestOtp(newNumber)}
                    >
                      <TranslatedText id="app.ok" />
                    </Button>
                  </>
                )}
                {step === 'decline' && (
                  <Button
                    onClick={close}
                    endIcon={
                      <CircularProgress
                        size={14}
                        thickness={5}
                        color="inherit"
                        variant="determinate"
                        value={(countdown / CLOSE_TIMEOUT) * 100}
                      />
                    }
                  >
                    <TranslatedText id="app.close" />
                  </Button>
                )}
                {step === 'failure' && (
                  <Button onClick={close}>
                    <TranslatedText id="app.close" />
                  </Button>
                )}
              </Stack>
            </DialogActions>
          </motion.div>
        )}
      </AnimatePresence>
    </StandardDialogContainer>
  );
};

export const PhoneVerificationDialogWithPrompt = () => {
  if (!AppConfigService.getInstance().phoneVerificationPromptDisplayable) {
    return null;
  }
  return <PhoneVerificationDialog initialPrompt />;
};

export const UpdatablePhoneVerificationDialog = () => (
  <PhoneVerificationDialog updatePhone />
);
